import { store } from 'store/index';

let events: Record<string, unknown>[] = [];
let previousPageUrl = undefined;

type TrackProductItem = {
	item_id: string;
	item_name: string;
	affiliation: string;
	coupon: string;
	discount: number;
	index?: number;
	item_brand: string;
	item_category: string;
	item_category2?: string;
	item_category3?: string;
	item_variant: string;
	item_list_id?: string;
	item_list_name?: string;
	item_cart_type?: string;
	price: number;
	quantity: number;
	calcModelId?: string;
	insuranceTerminate?: boolean; // !!doesn't make sense if quantity is more than 1 or for family insurance
	insuranceActivationStart?: string; // !! doesn't make sense if quantity is more than 1 or for family insurance

	// custom variables
	// car, contents(home), house, cottage, family of children
	excess?: number;
	// travel - europa/verden
	insuranceCoverage?: string;
	// accident
	workType?: string;
	// childAccident
	numberOfChildren?: number;
};

export const trackEvent = (eventObject: Record<string, unknown>, pageViewEvent?: boolean) => {
	if (pageViewEvent) {
		events.unshift(eventObject); // Insert page view events at the beginning of the array
	} else {
		events.push(eventObject);
	}
};

export const pushToDataLayer = () => {
	if (window?.dataLayer) {
		window.dataLayer.push(...events);
		events = [];
	} else {
		console.error('DataLayer not found!');
	}
};

const getProductItems = (metaArea: MetaArea) => {
	const prices = getPrices();
	const productNames = [metaArea?.items[0]?.content?.productName];

	const items = productNames.map((productName, index) => ({
		item_id: productName,
		item_name: productName,
		affiliation: 'online',
		coupon: 'one insurance',
		discount: 0,
		index,
		item_brand: 'codan',
		item_category: metaArea.items[0].content.itemCategory,
		item_category2: metaArea.items[0].content.itemCategory2,
		item_category3: metaArea.items[0].content.itemCategory3,
		item_variant: 'basic coverage', // ?
		price: prices[index],
		quantity: productNames?.length ?? 1,
	}));
	return items;
};

export const trackPageView = (url: string, brand: string) => {
	if (url !== previousPageUrl) {
		// Track pageview event
		trackEvent(
			{
				event: 'trackPageview',
			},
			true
		);
		// Last event will be first in event array
		trackEvent(
			{
				pageSection: 'privat',
				brand,
				previousPageUrl,
				platform: 'web',
				fullUrl: window.location.href,
			},
			true
		);

		previousPageUrl = url;
	}
};

/**
 *
 * ViewItem (Package page )
  Whenever the user views an item on the "choose package" step in the flow, the following custom  
  event should be dispatched:  

  //BeginCheckout (calculation start) efter valg af pakke
  (calculation start)
		Whenever the user enters the first step in the checkout process, the following custom event should
		be dispatched
 */
export const trackBeginCheckoutViewItem = (metaArea: MetaArea, isCheckout: boolean) => {
	try {
		const event = {
			event: isCheckout ? 'begin_checkout' : 'view_item',
			ecommerce: {
				currency: 'DKK',
				value: 0,
				items: getProductItems(metaArea),
			},
		};
		trackEvent({ ecommerce: null }); // Clear the previous ecommerce object.
		trackEvent(event);
	} catch (e) {
		console.error('Error tracking view item event:', e);
		// too bad
	}
};

/*
4.10. Add Shipping Info
Whenever the user lands on the "personal information" step in the flow, the following custom event
should be dispatched:
*/
export const trackAddShippingInfo = (metaArea: MetaArea) => {
	try {
		const event = {
			event: 'add_shipping_info',
			ecommerce: {
				currency: 'DKK',
				value: getPrices()[0],
				items: getProductItems(metaArea),
			},
		};
		trackEvent({ ecommerce: null }); // Clear the previous ecommerce object.
		trackEvent(event);
	} catch (e) {
		console.error('Error tracking add shipping info event:', e);
		// too bad
	}
};

/* 4.11. Add Payment Info
Whenever the user lands on the "payment" step in the flow the following custom event should be
dispatched:*/
export const trackAddPaymentInfo = (metaArea: MetaArea) => {
	try {
		const event = {
			event: 'add_payment_info',
			ecommerce: {
				currency: 'DKK',
				value: getPrices()[0], //price.reduce((acc, price) => acc + price, 0),
				payment_type: 'Bank account',
				items: getProductItems(metaArea),
			},
		};
		trackEvent({ ecommerce: null }); // Clear the previous ecommerce object.
		trackEvent(event);
	} catch (e) {
		console.error('Error tracking add payment info event:', e);
		// too bad
	}
};

/*4.12. Purchase
Whenever the user completes a purchase, the following custom event should be dispatched:*/
export const trackPurchase = async (metaArea: MetaArea) => {
	try {
		const products: Array<TrackProductItem> = getProductItems(metaArea);
		let yearlyPriceTotalAllProducts = 0;
		let yearlyStatutoryFeeAllProducts = 0;
		const calculatorParameterPrefix = metaArea?.items[0]?.content?.calculatorParameterPrefix;
		products.forEach((product, index) => {
			yearlyPriceTotalAllProducts += getPrices()[index] || 0;
			doProductMapping(product, calculatorParameterPrefix);
		});

		const monthly = getStoreValue('SELECTED_PACKAGE_PAYMENT_FREQUENCY') === '12';

		const userData = await getUserData();
		const event = {
			event: 'purchase',
			userData,
			insuranceData: getInsuranceData(monthly, calculatorParameterPrefix),
			ecommerce: {
				transaction_id: Date.now() + '',
				currency: 'DKK',
				tax: yearlyStatutoryFeeAllProducts,
				value: yearlyPriceTotalAllProducts,
				payment_type: 'Bank account',
				products,
			},
		};
		trackEvent({ ecommerce: null }); // Clear the previous ecommerce object.
		trackEvent(event);
	} catch (e) {
		// too bad
		console.error('Error tracking trackPurchase event:', e);
	}
};

const doProductMapping = (product, calculatorParameterPrefix: string) => {
	if (product) {
		product.insuranceTerminate = getStoreValue('CUSTOMER_HAS_FORMER_COMPANY') === 'Y';
		product.insuranceActivationStart = getStoreValue('COVER_START');
		handleExcess(product);
		// travel
		product.insuranceCoverage = getStoreValue(calculatorParameterPrefix + '_COVER_RANGE') ?? undefined;
		product.numberOfChildren = getStoreValue(calculatorParameterPrefix + '_NUMBER_CHILDREN') ?? undefined;

		// cleanup ProductItem
		product.calcModelId = undefined;

		// accident
		product.workType = getStoreValue(calculatorParameterPrefix + '_LABOUR_TYPE') ?? undefined;
	}
};

const handleExcess = (product) => {
	const excessValue = getStoreValue('SELECTED_PACKAGE_EXCESS');
	if (excessValue) {
		product.excess = excessValue;
	}
};

const getPrices = () => {
	const monthly = getStoreValue('SELECTED_PACKAGE_PAYMENT_FREQUENCY') === '12';
	const price = monthly
		? [getStoreValue('SELECTED_PACKAGE_MONTHLY_PRICE')]
		: [getStoreValue('SELECTED_PACKAGE_YEARLY_PRICE')];
	return price ?? 0;
};
const getInsuranceData = (monthly: boolean, calculatorParameterPrefix: string) => {
	const adults = getStoreValue(calculatorParameterPrefix + '_NUMBER_ADULTS') ?? 0;
	const children = getStoreValue(calculatorParameterPrefix + '_NUMBER_CHILDREN') ?? 0;
	return {
		insuranceRegistered: !!getStoreValue('CUSTOMER_ID'),
		insurancePeopleInHousehold: Number.parseInt(adults) + Number.parseInt(children),
		insuranceAge: getStoreValue('CUSTOMER_AGE'),
		insuranceClaimsNumber: getStoreValue(calculatorParameterPrefix + '_CAQ_CLAIMS_THIS_OBJECT') ?? 0,
		insurancePaymentRate: monthly ? 'Monthly' : 'Yearly',
	};
};

const getUserData = async () => {
	const fullName = getStoreValue('CUSTOMER_FULL_NAME')?.trim();
	const nameArray = fullName.split(' ');
	let firstName = nameArray[0];
	const lastName = nameArray.length > 1 ? nameArray[nameArray.length - 1] : 'NA';
	if (lastName !== 'NA') {
		firstName = fullName.replaceAll(' ' + lastName, '');
	}
	const address = JSON.parse(getStoreValue('CUSTOMER_FULL_ADDRESS') || '{}');
	return {
		userId: await toSHA256(getStoreValue('CUSTOMER_EMAIL')),
		phoneNumber: await toSHA256(E164format(getStoreValue('CUSTOMER_PHONE'))),
		city: await toSHA256(address?.postnrnavn),
		street: await toSHA256(address?.vejnavn),
		country: await toSHA256('DK'),
		postCode: await toSHA256(address?.postnr),
		firstname: await toSHA256(firstName),
		lastname: await toSHA256(lastName),
	};
};

const E164format = (phoneNo) => {
	if (!phoneNo) {
		return '';
	}
	const cleanPhone = phoneNo.trim().replaceAll(' ', '');
	if (!cleanPhone.startsWith('+45')) {
		return '+45' + cleanPhone;
	}
	return cleanPhone;
};
const toSHA256 = async (message): Promise<string> => {
	if (!message) {
		return undefined;
	}
	const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
	const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8); // hash the message
	const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
	const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
	return hashHex;
};
const getStoreValue = (key: string) => {
	return store.getState().calculatorForm[key]?.data?.value;
};
